'use client'

// Landing plan quiz — guides a visitor to the right membership before the
// explanatory pricing. Price inputs (baths, A/C, pool, irrigation) drive the
// estimate; the plan tier is decided by residency / property-manager /
// vacation-rental. DESIGN.md styling, blue as the single action voice.

import { useState, useEffect, useRef } from 'react'
import posthog from 'posthog-js'
import Image from 'next/image'
import './plan-quiz.css'
import { submitQuizLead } from './plan-quiz-actions'
import type { QuizLead } from './plan-quiz-types'
import { CATALOG, SYSTEM_ORDER, SYSTEM_LABELS, planIncludes, type CatalogTier } from '@/lib/service-catalog'

type Lang = 'en' | 'es'
type PlanKey = 'esencial' | 'cuidado' | 'memoria'
const WA = '50670464915'

// Quiz plan keys ↔ catalog tiers, and how often we visit per plan.
const PLAN_TIER:   Record<PlanKey, CatalogTier> = { esencial: 'essential', cuidado: 'premium', memoria: 'elite' }
const PLAN_VISITS: Record<PlanKey, number>      = { esencial: 1, cuidado: 2, memoria: 4 }

type Opt = { l: string; v?: number; k?: string }
type Q = {
  id: string; tag: string; q: string; help: string; cols: 2 | 4
  type: 'single' | 'multi' | 'number'; opts: Opt[]
  def?: number; min?: number; max?: number; hint?: string
}

const QUESTIONS: Record<Lang, Q[]> = {
  en: [
    { id:'worry', tag:'Your concerns', cols:2, type:'multi', q:'What worries you most?', help:'Select as many as apply — this is what we look at first.', opts:[{l:'Mould & humidity'},{l:'Leaks & plumbing'},{l:'Electrical & surges'},{l:'A/C failing when guests arrive'},{l:'The pool going green'},{l:'Termites & wood rot'},{l:'Roof leaks in green season'},{l:'Security while I’m away'},{l:'Nobody to call'}] },
    { id:'live', tag:'How it’s used', cols:2, type:'single', q:'Do you live in Nosara?', help:'Someone on the ground changes what the house needs.', opts:[{l:'Yes — full or part of the year',k:'yes'},{l:'No, I live elsewhere',k:'no'}] },
    { id:'pm', tag:'Who watches it', cols:2, type:'single', q:'Do you have a property manager?', help:'Someone already keeping an eye on the house.', opts:[{l:'Yes, I have one',k:'yes'},{l:'No property manager',k:'no'}] },
    { id:'rent', tag:'How it’s used', cols:2, type:'single', q:'Do you rent it as a vacation rental when you’re away?', help:'Guests coming and going add wear and risk.', opts:[{l:'Yes, vacation rental',k:'yes'},{l:'No, not rented',k:'no'}] },
    { id:'baths', tag:'Your house', cols:2, type:'number', def:2, min:0, max:20, q:'How many bathrooms?', help:'Count every full and half bath.', hint:'First 2.5 bathrooms included · +$30/mo each extra', opts:[] },
    { id:'ac', tag:'Cooling', cols:2, type:'number', def:4, min:0, max:20, q:'How many A/C units?', help:'Split units, mini-splits, anything with a filter.', hint:'First 4 A/C included · +$30/mo each extra', opts:[] },
    { id:'pool', tag:'Water', cols:2, type:'single', q:'Is there a pool?', help:'We check filters and pumps — not pool cleaning.', opts:[{l:'No pool',k:'no'},{l:'Yes, there’s a pool',k:'yes'}] },
    { id:'irrig', tag:'Water', cols:2, type:'single', q:'Is there an irrigation system?', help:'System check only — no landscaping.', opts:[{l:'No',k:'no'},{l:'Yes',k:'yes'}] },
    { id:'roof', tag:'Structure', cols:2, type:'single', q:'What kind of roof? (for reference)', help:'Helps us know what to look at first — it doesn’t change the price.', opts:[{l:'Tile'},{l:'Metal / zinc'},{l:'Concrete slab'},{l:'Palapa / thatch'},{l:'Not sure'}] },
  ],
  es: [
    { id:'worry', tag:'Tus preocupaciones', cols:2, type:'multi', q:'¿Qué te preocupa más?', help:'Elegí las que apliquen — esto es lo que miramos primero.', opts:[{l:'Moho y humedad'},{l:'Fugas y plomería'},{l:'Eléctrico y picos'},{l:'A/C fallando con huéspedes'},{l:'La piscina poniéndose verde'},{l:'Comején y madera'},{l:'Goteras en época verde'},{l:'Seguridad cuando no estoy'},{l:'No tener a quién llamar'}] },
    { id:'live', tag:'Cómo se usa', cols:2, type:'single', q:'¿Vivís en Nosara?', help:'Que haya alguien cerca cambia lo que la casa necesita.', opts:[{l:'Sí — todo o parte del año',k:'yes'},{l:'No, vivo en otro lado',k:'no'}] },
    { id:'pm', tag:'Quién la cuida', cols:2, type:'single', q:'¿Tenés property manager?', help:'Alguien que ya está pendiente de la casa.', opts:[{l:'Sí, tengo uno',k:'yes'},{l:'No tengo',k:'no'}] },
    { id:'rent', tag:'Cómo se usa', cols:2, type:'single', q:'¿La rentás como alquiler vacacional cuando no estás?', help:'Huéspedes entrando y saliendo suman desgaste y riesgo.', opts:[{l:'Sí, alquiler vacacional',k:'yes'},{l:'No, no se alquila',k:'no'}] },
    { id:'baths', tag:'Tu casa', cols:2, type:'number', def:2, min:0, max:20, q:'¿Cuántos baños?', help:'Contá baños completos y medios baños.', hint:'Primeros 2.5 baños incluidos · +$30/mes c/u extra', opts:[] },
    { id:'ac', tag:'Aires', cols:2, type:'number', def:4, min:0, max:20, q:'¿Cuántos aires acondicionados?', help:'Splits, mini-splits, todo lo que tenga filtro.', hint:'Primeros 4 A/C incluidos · +$30/mes c/u extra', opts:[] },
    { id:'pool', tag:'Agua', cols:2, type:'single', q:'¿Hay piscina?', help:'Revisamos filtros y bombas — no incluye limpieza.', opts:[{l:'Sin piscina',k:'no'},{l:'Sí, hay piscina',k:'yes'}] },
    { id:'irrig', tag:'Agua', cols:2, type:'single', q:'¿Hay sistema de riego?', help:'Solo revisión del sistema — sin jardinería.', opts:[{l:'No',k:'no'},{l:'Sí',k:'yes'}] },
    { id:'roof', tag:'Estructura', cols:2, type:'single', q:'¿Qué tipo de techo? (referencia)', help:'Nos ayuda a saber qué mirar primero — no cambia el precio.', opts:[{l:'Teja'},{l:'Metal / zinc'},{l:'Losa de concreto'},{l:'Palapa'},{l:'No estoy seguro'}] },
  ],
}

const PLAN_META: Record<PlanKey, { name: string; base: number; services: number }> = {
  esencial: { name: 'Esencial',      base: 350, services: 23 },
  cuidado:  { name: 'Cuidado',       base: 550, services: 38 },
  memoria:  { name: 'Memoria total', base: 900, services: 58 },
}

const PLAN_COPY: Record<Lang, Record<PlanKey, { forYou: string; why: string; list: string[] }>> = {
  en: {
    esencial: {
      forYou:'You keep an eye on the house (or someone you trust does), and it isn’t rented.',
      why:'We put the systems that fail quietly on a fixed schedule and give you a direct line — the essentials handled, without handing over the whole house.',
      list:['Monthly maintenance by the in-house team','Plumbing, electrical & A/C on schedule','Photo report after every visit','Direct WhatsApp line','Emergencies on request (not included)','Month to month — cancel any time'] },
    cuidado: {
      forYou:'You have a property manager or rent the house, and want the upkeep held to the same standard whether you’re here or not.',
      why:'You delegate the day to day, but your home’s maintenance stays at the same level. Prevention that’s cheaper than repair, with an engineer checking every quarter.',
      list:['Everything in Esencial, tighter schedule','Pool, irrigation & garden supervised','Quarterly inspection by the founding engineer','1 emergency call/month included · priority response','Casa Memory property file & health score'] },
    memoria: {
      forYou:'You want full control of everything that happens to your house, even when you’re away.',
      why:'The house sits without you, so we become the eyes on the ground — every system, on the tightest schedule, documented for life.',
      list:['Everything in Cuidado, highest frequency','Full annual schedule across all systems','1 emergency call/month included · under-2-hour response','Pool, irrigation, roof, septic & solar included','Annual documented property file & report'] },
  },
  es: {
    esencial: {
      forYou:'Estás pendiente de la casa (o alguien de confianza), y no la alquilás.',
      why:'Ponemos en calendario fijo los sistemas que fallan en silencio y te damos una línea directa — lo esencial cubierto, sin entregar toda la casa.',
      list:['Mantenimiento mensual con el equipo propio','Plomería, eléctrico y A/C en calendario','Reporte con fotos en cada visita','Línea directa de WhatsApp','Emergencias a solicitud (no incluidas)','Mes a mes — cancelás cuando quieras'] },
    cuidado: {
      forYou:'Tenés property manager o la alquilás, y querés el mismo cuidado del mantenimiento estés o no.',
      why:'Delegás el día a día, pero el mantenimiento de tu casa se mantiene al mismo estándar. Prevención más barata que la reparación, con un ingeniero revisando cada trimestre.',
      list:['Todo lo de Esencial, calendario más frecuente','Piscina, riego y jardín supervisados','Inspección trimestral del ingeniero fundador','1 emergencia al mes incluida · respuesta prioritaria','Ficha Casa Memory y health score'] },
    memoria: {
      forYou:'Querés control total de todo lo que pasa en tu casa, aunque no estés.',
      why:'La casa está sin vos, así que somos los ojos en el terreno — todos los sistemas, en el calendario más frecuente, documentado de por vida.',
      list:['Todo lo de Cuidado, máxima frecuencia','Calendario anual completo de todos los sistemas','1 emergencia al mes incluida · respuesta en <2 horas','Piscina, riego, techo, séptico y solar incluidos','Ficha anual documentada y reporte'] },
  },
}

const UI: Record<Lang, Record<string, string>> = {
  en: { eyebrow:'Not sure which one? · 60 seconds', title:'Which plan does your house actually need?', sub:'Every house in Nosara wears differently. A few questions and we’ll tell you which membership fits.',
    qof:'Question', of:'of', back:'← Back', next:'Continue →', last:'See my plan →', pickErr:'Pick at least one to continue.',
    lastStep:'Last step', where:'Where should we send your plan?', whereHelp:'Add your details and we’ll show your exact price + book the free first inspection.',
    fname:'Your name', fphone:'WhatsApp number', femail:'Email', farea:'Where is the house?', fwhen:'When would you want to start?', fnote:'Anything we should look at first? (optional)',
    reveal:'Show my plan →', formErr:'Please add your name and a WhatsApp number or email.', chooseArea:'Choose an area…', soFar:'So far', priceLabel:'Your custom price', priceTease:'revealed at the end',
    recFor:'Recommended for your house', yourSelection:'Your selection', switchLabel:'Prefer another plan? Switch and see the price', recommendedTag:'Recommended', forYouLabel:'For you if', whatsIncluded:'What this plan includes', visitsMo:'visits/month', perYear:'services a year', estMonthly:'Estimated monthly', priceFine:'Based on your systems and extras. Confirmed exactly at your free first inspection — and it only goes down from there, never up.',
    first6:'Your first 6 months', off10:'10% off for 6 months', freeInsp:'Free first inspection within 7 days', waBtn:'Send this to The Repair Club →', waFine:'Opens WhatsApp with your answers written out. A person replies the same day.', restart:'Start over',
    pmUpsell:'You have a property manager — if you’d rather we take that over entirely, ask us about Memoria total.' },
  es: { eyebrow:'¿No sabés cuál? · 60 segundos', title:'¿Qué plan necesita tu casa realmente?', sub:'Cada casa en Nosara se desgasta distinto. Unas preguntas y te decimos qué membresía te calza.',
    qof:'Pregunta', of:'de', back:'← Atrás', next:'Continuar →', last:'Ver mi plan →', pickErr:'Elegí al menos una para continuar.',
    lastStep:'Último paso', where:'¿A dónde te enviamos tu plan?', whereHelp:'Dejanos tus datos y te mostramos tu precio exacto + la inspección gratis.',
    fname:'Tu nombre', fphone:'WhatsApp', femail:'Correo', farea:'¿Dónde está la casa?', fwhen:'¿Cuándo querés empezar?', fnote:'¿Algo que debamos mirar primero? (opcional)',
    reveal:'Ver mi plan →', formErr:'Agregá tu nombre y un WhatsApp o correo.', chooseArea:'Elegí un área…', soFar:'Hasta ahora', priceLabel:'Tu precio personalizado', priceTease:'se revela al final',
    recFor:'Recomendado para tu casa', yourSelection:'Tu selección', switchLabel:'¿Preferís otro plan? Cambialo y mirá el precio', recommendedTag:'Recomendado', forYouLabel:'Para vos si', whatsIncluded:'Qué incluye este plan', visitsMo:'visitas/mes', perYear:'servicios al año', estMonthly:'Estimado mensual', priceFine:'Según tus sistemas y extras. Se confirma exacto en tu inspección gratis — y de ahí solo baja, nunca sube.',
    first6:'Tus primeros 6 meses', off10:'10% de descuento por 6 meses', freeInsp:'Primera inspección gratis en 7 días', waBtn:'Enviar a The Repair Club →', waFine:'Abre WhatsApp con tus respuestas listas. Una persona responde el mismo día.', restart:'Empezar de nuevo',
    pmUpsell:'Tenés property manager — si preferís que nosotros asumamos ese rol, preguntanos por Memoria total.' },
}

const AREAS = ['Playa Guiones','Playa Pelada','Playa Garza','Las Delicias','Nosara / Esperanza','Elsewhere near Nosara']
const WHENS: Record<Lang, string[]> = {
  en: ['As soon as possible','Within the next month','Before I travel back','Just gathering information'],
  es: ['Lo antes posible','En el próximo mes','Antes de volver a viajar','Solo estoy averiguando'],
}

const Check = () => <svg viewBox="0 0 24 24"><path d="M4 12.5 9.5 18 20 6.5"/></svg>

export default function PlanQuiz({ lang }: { lang: Lang }) {
  const Q = QUESTIONS[lang]; const t = UI[lang]
  const [step, setStep] = useState(0)
  const [phase, setPhase] = useState<'q' | 'form' | 'result'>('q')
  const [ans, setAns] = useState<Record<string, Opt | Opt[]>>(() => {
    const seed: Record<string, Opt> = {}
    for (const q of Q) if (q.type === 'number') seed[q.id] = { l: String(q.def ?? 0), v: q.def ?? 0 }
    return seed
  })
  const [err, setErr] = useState(false)
  const [form, setForm] = useState({ name:'', phone:'', email:'', area:'', when: WHENS[lang][0], note:'' })
  const [ferr, setFerr] = useState(false)
  // In the result, the member can switch plans; null = show the recommended one.
  const [chosenPlan, setChosenPlan] = useState<PlanKey | null>(null)

  // Track when a visitor lands on the quiz (via a #quiz CTA or a direct
  // /#quiz load). Fires once per page load; hashchange doesn't fire on the
  // initial load so we also check the hash on mount.
  const quizTracked = useRef(false)
  useEffect(() => {
    const track = () => {
      if (window.location.hash === '#quiz' && !quizTracked.current) {
        quizTracked.current = true
        posthog.capture('quiz_viewed')
      }
    }
    track()
    window.addEventListener('hashchange', track)
    return () => window.removeEventListener('hashchange', track)
  }, [])

  // Step-by-step funnel: fire an event every time the visitor reaches a new
  // question / the lead form / the result, so PostHog can show exactly where
  // people drop off inside the quiz.
  useEffect(() => {
    if (phase === 'q') {
      posthog.capture('quiz_step_viewed', {
        step_number: step + 1,      // 1-based, easy to read in PostHog
        step_id:     Q[step]?.id,   // worry, live, pm, rent, baths, ac, pool, irrig, roof
        total_steps: Q.length,
      })
    } else if (phase === 'form') {
      posthog.capture('quiz_form_viewed')       // reached the lead form
    } else if (phase === 'result') {
      posthog.capture('quiz_completed')         // saw the recommended plan
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, phase])

  const val = (id: string, def = 0) => { const a = ans[id]; return a && !Array.isArray(a) ? (a.v ?? def) : def }
  const keyOf = (id: string) => { const a = ans[id]; return a && !Array.isArray(a) ? a.k : undefined }
  const labelOf = (id: string) => { const a = ans[id]; if (!a) return '—'; return Array.isArray(a) ? (a.map(o => o.l).join(', ') || '—') : a.l }

  function recommend(): { plan: PlanKey; pmUpsell: boolean } {
    const live = keyOf('live') === 'yes', pm = keyOf('pm') === 'yes', rent = keyOf('rent') === 'yes'
    let plan: PlanKey
    if (rent) plan = (!live && !pm) ? 'memoria' : 'cuidado'
    else plan = (!live && !pm) ? 'memoria' : 'esencial'
    return { plan, pmUpsell: pm && !rent && plan === 'esencial' }
  }
  function estimate(plan: PlanKey): number {
    let n = PLAN_META[plan].base
    n += 30 * Math.max(0, val('baths') - 2)
    n += 30 * Math.max(0, val('ac') - 4)
    const hasWater = keyOf('pool') === 'yes' || keyOf('irrig') === 'yes'
    if (hasWater && plan !== 'memoria') n += 50
    return n
  }

  function setNum(q: Q, delta: number) {
    const cur = val(q.id, q.def ?? 0)
    const n = Math.max(q.min ?? 0, Math.min(q.max ?? 20, cur + delta))
    setErr(false)
    setAns(a => ({ ...a, [q.id]: { l: String(n), v: n } }))
  }

  function pick(q: Q, o: Opt) {
    setErr(false)
    if (q.type === 'single') {
      setAns(a => ({ ...a, [q.id]: o }))
      setTimeout(() => advance(o), 180)
    } else {
      setAns(a => {
        const cur = Array.isArray(a[q.id]) ? (a[q.id] as Opt[]) : []
        const on = cur.some(x => x.l === o.l)
        return { ...a, [q.id]: on ? cur.filter(x => x.l !== o.l) : cur.concat([o]) }
      })
    }
  }
  function advance(justPicked?: Opt) {
    const q = Q[step]; const a = justPicked ?? ans[q.id]
    const empty = a === undefined || (Array.isArray(a) && a.length === 0)
    if (empty) { setErr(true); return }
    if (step < Q.length - 1) setStep(s => s + 1)
    else setPhase('form')
  }

  function reveal() {
    if (!form.name.trim() || (!form.phone.trim() && !form.email.trim())) { setFerr(true); return }
    setFerr(false)
    const { plan } = recommend(); const est = estimate(plan); const meta = PLAN_META[plan]
    const summary = Q.map(q => `${q.tag} · ${q.q.replace(/\?.*/, '')}: ${labelOf(q.id)}`).join('\n')

    const payload: QuizLead = {
      name: form.name, phone: form.phone, email: form.email, area: form.area, when: form.when, note: form.note,
      plan, planLabel: meta.name, estimate: est, summary,
      baths: val('baths', 2), ac: val('ac', 4), pool: keyOf('pool') === 'yes', riego: keyOf('irrig') === 'yes',
    }
    submitQuizLead(payload).catch(() => {})   // fire-and-forget; WA is the primary hand-off
    setPhase('result')
  }

  const rec = recommend()
  const activePlan: PlanKey = chosenPlan ?? rec.plan
  const est  = estimate(activePlan)
  const meta = PLAN_META[activePlan]
  const pc   = PLAN_COPY[lang][activePlan]

  // WhatsApp hand-off reflects the plan currently shown (recommended or switched).
  function waHrefFor(plan: PlanKey): string {
    const m = PLAN_META[plan]; const e = estimate(plan)
    const summary = Q.map(q => `${q.tag} · ${q.q.replace(/\?.*/, '')}: ${labelOf(q.id)}`).join('\n')
    const msg = [
      lang === 'es' ? 'Hola Repair Club — llené el cuestionario en su sitio.' : 'Hi Repair Club — I filled out the quiz on your site.',
      '', `NAME: ${form.name}`, `WHATSAPP: ${form.phone || '—'}`, `EMAIL: ${form.email || '—'}`,
      `AREA: ${form.area || '—'}`, `START: ${form.when}`, '', summary,
      form.note ? `\nNOTES: ${form.note}` : '',
      '', `Plan: ${m.name} (~$${e}/mo + IVA)`,
      lang === 'es' ? 'Quiero la inspección gratis y el 10% x 6 meses.' : 'I’d like the free inspection and 10% off for 6 months.',
    ].join('\n')
    return `https://wa.me/${WA}?text=${encodeURIComponent(msg)}`
  }
  const waHref = waHrefFor(activePlan)
  const money = (n: number) => '$' + n.toLocaleString('en-US')
  const railPct = phase === 'result' ? 100 : phase === 'form' ? (Q.length / (Q.length + 1)) * 100 : (step / (Q.length + 1)) * 100

  return (
    <section className="pq" id="quiz">
      <div className="pq-shell">
        <div className="pq-head">
          <span className="pq-eyebrow">{t.eyebrow}</span>
          <h2>{t.title}</h2>
          <p>{t.sub}</p>
        </div>
        <div className="pq-panel">
          <div className="pq-rail"><i style={{ width: railPct + '%' }} /></div>

          {phase === 'q' && (() => { const q = Q[step]; return (
            <div className="pq-in">
              <div className="pq-meta"><span>{t.qof} {step + 1} {t.of} {Q.length}</span><span>{q.tag}</span></div>
              <h3 className="pq-title">{q.q}</h3>
              <p className="pq-help">{q.help}</p>
              {q.type === 'number' ? (
                <div className="pq-stepwrap">
                  <div className="pq-stepper">
                    <button type="button" className="pq-step-btn" onClick={() => setNum(q, -1)} aria-label="−" disabled={val(q.id, q.def ?? 0) <= (q.min ?? 0)}>−</button>
                    <span className="pq-step-val" aria-live="polite">{val(q.id, q.def ?? 0)}{val(q.id, q.def ?? 0) >= (q.max ?? 20) ? '+' : ''}</span>
                    <button type="button" className="pq-step-btn" onClick={() => setNum(q, 1)} aria-label="+">+</button>
                  </div>
                  {q.hint && <p className="pq-step-hint">{q.hint}</p>}
                </div>
              ) : (
                <div className={`pq-opts c${q.cols}`}>
                  {q.opts.map(o => {
                    const cur = ans[q.id]
                    const on = q.type === 'single' ? (cur && !Array.isArray(cur) && cur.l === o.l) : (Array.isArray(cur) && cur.some(x => x.l === o.l))
                    return (
                      <button key={o.l} type="button" className="pq-opt" aria-pressed={on ? 'true' : 'false'} onClick={() => pick(q, o)}>
                        <span className="pq-tick"><Check /></span><span>{o.l}</span>
                      </button>
                    )
                  })}
                </div>
              )}
              {err && <p className="pq-err">{t.pickErr}</p>}
              <div className="pq-sofar">
                <span className="pq-sofar-lab">{t.priceLabel}</span>
                <span className="pq-sofar-val">{t.priceTease}</span>
              </div>
              <div className="pq-foot">
                {step > 0 && <button className="pq-linkbtn" onClick={() => setStep(s => s - 1)}>{t.back}</button>}
                <button className="pq-btn" style={{ marginLeft: 'auto' }} onClick={() => advance()}>{step === Q.length - 1 ? t.last : t.next}</button>
              </div>
            </div>
          )})()}

          {phase === 'form' && (
            <div className="pq-in">
              <div className="pq-meta"><span>{t.lastStep}</span></div>
              <h3 className="pq-title">{t.where}</h3>
              <p className="pq-help">{t.whereHelp}</p>
              <div className="pq-fields">
                <div className="pq-field"><label>{t.fname}</label><input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} autoComplete="name" /></div>
                <div className="pq-field"><label>{t.fphone}</label><input type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+506 8888 1234" autoComplete="tel" /></div>
                <div className="pq-field"><label>{t.femail}</label><input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} autoComplete="email" /></div>
                <div className="pq-field"><label>{t.farea}</label><select value={form.area} onChange={e => setForm(f => ({ ...f, area: e.target.value }))}><option value="">{t.chooseArea}</option>{AREAS.map(a => <option key={a}>{a}</option>)}</select></div>
                <div className="pq-field full"><label>{t.fwhen}</label><select value={form.when} onChange={e => setForm(f => ({ ...f, when: e.target.value }))}>{WHENS[lang].map(w => <option key={w}>{w}</option>)}</select></div>
                <div className="pq-field full"><label>{t.fnote}</label><textarea value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} /></div>
              </div>
              {ferr && <p className="pq-err">{t.formErr}</p>}
              <div className="pq-foot">
                <button className="pq-linkbtn" onClick={() => setPhase('q')}>{t.back}</button>
                <button className="pq-btn" style={{ marginLeft: 'auto' }} onClick={reveal}>{t.reveal}</button>
              </div>
            </div>
          )}

          {phase === 'result' && (
            <div className="pq-result">
              <div className="pq-res-l">
                <div className="pq-res-head">
                  <Image src="/logo.png" alt="The Repair Club" width={96} height={96} className="pq-logo" />
                  <span className="pq-tag"><Check /> {activePlan === rec.plan ? t.recFor : t.yourSelection}</span>
                </div>

                {/* Plan switcher — recompute price/services/copy for the chosen
                    plan using the A/C + bathrooms already entered. */}
                <p className="pq-switch-lab">{t.switchLabel}</p>
                <div className="pq-switch" role="tablist" aria-label={t.switchLabel}>
                  {(['esencial', 'cuidado', 'memoria'] as PlanKey[]).map(k => (
                    <button
                      key={k}
                      type="button"
                      role="tab"
                      aria-selected={activePlan === k}
                      className={`pq-switch-pill ${activePlan === k ? 'is-active' : ''}`}
                      onClick={() => setChosenPlan(k)}
                    >
                      <span>{PLAN_META[k].name}</span>
                      <small>{money(estimate(k))}/mo</small>
                      {k === rec.plan && <em className="pq-switch-rec">{t.recommendedTag}</em>}
                    </button>
                  ))}
                </div>

                <div className="pq-plan-name">{meta.name}</div>
                <p className="pq-plan-foryou"><b>{t.forYouLabel}:</b> {pc.forYou}</p>
                <p className="pq-plan-why">{pc.why}</p>
                <ul className="pq-plan-list">{pc.list.map(x => <li key={x}><Check />{x}</li>)}</ul>

                {/* What this plan includes — the catalog tasks for the active
                    plan, grouped by system, with the visit rhythm. */}
                <details className="pq-cat">
                  <summary>
                    {t.whatsIncluded}
                    <span className="pq-cat-meta">{PLAN_VISITS[activePlan]} {t.visitsMo} · {meta.services} {t.perYear}</span>
                  </summary>
                  <div className="pq-cat-body">
                    {SYSTEM_ORDER.map(sys => {
                      const items = CATALOG.filter(s => s.system === sys && planIncludes(s.from, PLAN_TIER[activePlan]))
                      if (items.length === 0) return null
                      return (
                        <div className="pq-cat-sys" key={sys}>
                          <div className="pq-cat-sys-head">
                            <span>{SYSTEM_LABELS[lang][sys]}</span>
                            <small>{items.length}</small>
                          </div>
                          <ul>{items.map(it => <li key={it.name}>{it.name}</li>)}</ul>
                        </div>
                      )
                    })}
                  </div>
                </details>
              </div>
              <div className="pq-res-r">
                <div className="pq-lab">{meta.services} {t.perYear}</div>
                <div className="pq-lab" style={{ marginTop: 14 }}>{t.estMonthly}</div>
                <div className="pq-price">{money(est)} <small>/ mo + IVA</small></div>
                <p className="pq-fine">{t.priceFine}</p>
                <div className="pq-hr" />
                <div className="pq-lab">{t.first6}</div>
                <div className="pq-price" style={{ color: 'var(--blue-pale, #DBE8F8)' }}>{money(Math.round(est * 0.9))} <small>/ mo</small></div>
                <div className="pq-offer"><svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><path d="M12 7v5.5l3.4 2"/></svg><span>{t.off10} · {t.freeInsp}</span></div>
                {rec.pmUpsell && <p className="pq-fine" style={{ marginTop: 14 }}>{t.pmUpsell}</p>}
                <a className="pq-btn pq-wa" href={waHref} target="_blank" rel="noopener noreferrer">{t.waBtn}</a>
                <p className="pq-fine">{t.waFine}</p>
                <button className="pq-linkbtn" style={{ marginTop: 4 }} onClick={() => { setAns({}); setStep(0); setChosenPlan(null); setPhase('q') }}>{t.restart}</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
